import type { LucideIcon } from 'lucide-react';

export interface ChoiceTile<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
  disabled?: boolean;
}

interface ChoiceTilesProps<T extends string> {
  legend: string;
  options: ChoiceTile<T>[];
  value: T | null;
  onChange: (value: T) => void;
  /** Compact tiles without icon height, e.g. for a two-way switch. */
  compact?: boolean;
}

const COLS: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' };

/**
 * One choice out of a few, as large tiles that work with a thumb — the role in the vehicle,
 * business or private trip. The selected tile is FF-green, a disabled one stays visible.
 */
export function ChoiceTiles<T extends string>({ legend, options, value, onChange, compact = false }: ChoiceTilesProps<T>) {
  return (
    <fieldset>
      <legend className="text-[13px] font-medium text-slate-700 mb-2">{legend}</legend>
      <div className={`grid gap-2 ${COLS[options.length] ?? 'grid-cols-3'}`}>
        {options.map(({ value: option, label, icon: Icon, disabled = false }) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onChange(option)}
              className={`flex ${compact ? 'min-h-[44px]' : 'min-h-[72px]'} flex-col items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-[13px] font-medium transition-colors ${
                selected
                  ? 'border-[#00A871] bg-emerald-50 text-emerald-800'
                  : disabled
                    ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {Icon && <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />}
              <span className="text-center leading-tight">{label}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
